/**
 * ═══════════════════════════════════════════════════════════════════
 * EnergiPro IMS — Centralized Product Configuration
 * Enterprise Edition | Version 1.0 Gold
 * ═══════════════════════════════════════════════════════════════════
 * 
 * Single source of truth for all product identity, version management,
 * release tracking, and module status across the entire application.
 * 
 * FROZEN: Version 1.0 Gold baseline. No structural modifications 
 * permitted without formal Change Control process.
 * ═══════════════════════════════════════════════════════════════════
 */

// ── PRODUCT IDENTITY ─────────────────────────────────────────────
export const PRODUCT = {
  name: 'EnergiPro IMS',
  edition: 'Enterprise',
  version: '1.0',
  versionLabel: 'Gold',
  fullVersion: 'Version 1.0 Gold',
  build: '2026.07.001',
  buildDate: '2026-06-29',
  releaseDate: '2026-06-29',
  releaseStatus: 'Production Ready',
  owner: 'EnergiPro Solutions LLC',
  copyright: `© ${new Date().getFullYear()} EnergiPro Solutions LLC`,
  license: 'Enterprise License',
  environment: 'Production',
  databaseVersion: '1.0.0',
  documentEngineVersion: '4.0.0',
} as const;

// ── VERSION DISPLAY STRINGS ──────────────────────────────────────
export const VERSION_STRINGS = {
  short: `v${PRODUCT.version}`,
  medium: `${PRODUCT.name} ${PRODUCT.fullVersion}`,
  full: `${PRODUCT.name}\n${PRODUCT.edition} Edition\n${PRODUCT.fullVersion}\nBuild ${PRODUCT.build}`,
  footer: `${PRODUCT.name} | ${PRODUCT.edition} Edition | ${PRODUCT.fullVersion}`,
  title: `${PRODUCT.name} — ${PRODUCT.edition}`,
  badge: `${PRODUCT.fullVersion} (${PRODUCT.build})`,
} as const;

// ── RELEASE MANAGEMENT ───────────────────────────────────────────
export type ReleaseStatus = 'Production' | 'RC' | 'Beta' | 'Alpha' | 'Planned' | 'In Development' | 'Deprecated';
export type ReleaseType = 'Major' | 'Minor' | 'Patch' | 'Hotfix' | 'Internal Build';

export interface Release {
  id: string;
  version: string;
  versionLabel: string;
  build: string;
  releaseDate: string;
  owner: string;
  status: ReleaseStatus;
  type: ReleaseType;
  features: string[];
  modules: string[];
  bugFixes: string[];
  knownIssues: string[];
  approvalStatus: 'Approved' | 'Pending' | 'Rejected';
  rollbackAvailable: boolean;
  deploymentDate: string;
  productionDate: string;
}

// ── RELEASE REGISTER ─────────────────────────────────────────────
export const RELEASE_REGISTER: Release[] = [
  {
    id: 'REL-1.1.0',
    version: '1.1.0',
    versionLabel: 'Finance Foundation',
    build: '2026.07.002',
    releaseDate: '2026-06-30',
    owner: 'EnergiPro Solutions LLC',
    status: 'Production',
    type: 'Minor',
    features: [
      'Enterprise Financial Master Data (WP-01)',
      'Production Chart of Accounts (5-Level, 188 accounts)',
      'Cost Centers (13) & Profit Centers (9)',
      'Multi-Company Finance Configuration (SA, UAE, EG)',
      'VAT Engine (SA 15%, UAE 5%, EG 14%) + WHT',
      'Currency Engine (SAR, AED, EGP, USD, EUR)',
      'Payment Terms (10 standard + custom)',
      'Banking Foundation (Al Rajhi, SNB, ADCB)',
      'Financial Numbering Engine (runtime, company-aware)',
      'Accounting Posting Engine Foundation (20 events, interfaces only)',
      'Finance ECC (15 sub-sections)',
      'Finance Audit Trail Framework',
    ],
    modules: ['Finance'],
    bugFixes: [],
    knownIssues: ['Exchange rates not populated (treasury ops not started)', 'Fiscal years empty (period mgmt in WP-03)'],
    approvalStatus: 'Approved',
    rollbackAvailable: true,
    deploymentDate: '2026-06-30',
    productionDate: '2026-06-30',
  },
  {
    id: 'REL-1.0',
    version: '1.0',
    versionLabel: 'Gold',
    build: '2026.07.001',
    releaseDate: '2026-06-29',
    owner: 'EnergiPro Solutions LLC',
    status: 'Production',
    type: 'Major',
    features: [
      'Complete Commercial Module (Opportunities, Clients, Proposals, Pipeline)',
      'Enterprise Document Engine v4.0',
      'Financial Document Engine (Invoices, Proforma, Credit Notes)',
      'Procurement Module (POs, Vendors, GRN, Inventory)',
      'Operations Module (Projects, Timesheets, Milestones)',
      'QHSE & IMS Module (Documents, Records, Audits, CAPA)',
      'Executive Dashboards (CEO, CFO, Controller)',
      'Role-Based Access Control (RBAC)',
      'Multi-currency Support with FX Rates',
      'Approval Governance Framework',
      'Backup & Disaster Recovery',
      'Email Notification Engine',
    ],
    modules: ['Commercial', 'Finance', 'Operations', 'Procurement', 'QHSE & IMS', 'HR', 'Executive', 'Document Control'],
    bugFixes: [],
    knownIssues: ['DNS SPF/DKIM verification pending', 'MailChannels deprecation risk assessment needed'],
    approvalStatus: 'Approved',
    rollbackAvailable: true,
    deploymentDate: '2026-06-29',
    productionDate: '2026-06-29',
  },
];

// ── CHANGELOG ────────────────────────────────────────────────────
export interface ChangelogEntry {
  version: string;
  date: string;
  sections: {
    newFeatures?: string[];
    enhancements?: string[];
    bugFixes?: string[];
    performance?: string[];
    security?: string[];
    database?: string[];
    api?: string[];
    breaking?: string[];
    migration?: string[];
    knownIssues?: string[];
  };
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '1.1.0 — Finance Foundation',
    date: '2026-06-30',
    sections: {
      newFeatures: [
        'WP-01: Finance Foundation & Enterprise Financial Master Data',
        'Production Chart of Accounts — 188 accounts, 5-level hierarchy, Oil & Gas specific',
        '13 Cost Centers covering all organizational departments',
        '9 Profit Centers aligned to business lines & geographies',
        'Multi-Company Finance: EnergiPro KSA, EPTS UAE, EnergiPro Egypt',
        'Tax Engine: SA VAT 15%, UAE VAT 5%, EG VAT 14%, WHT 5/15/20%',
        'Currency Engine: SAR, AED, EGP, USD, EUR with exchange rate types',
        '10 Payment Terms (100% Advance through Net 60 + Custom)',
        '5 Payment Methods (Bank Transfer, Cheque, Cash, LC, Electronic)',
        '8 Journal Types (GL, AR, AP, CM, FA, PA, IC, ADJ)',
        '6 Numbering Sequences (runtime, company-aware, pattern-driven)',
        'Banking Foundation: Al Rajhi, SNB, ADCB with SWIFT/IBAN schema',
        'Accounting Posting Engine Foundation: 20 events, 3 default rules',
        'Finance ECC: 15 configuration sub-sections',
        'Finance Audit Trail: full modification history framework',
        'Finance Security: 6 roles (Admin, Manager, Chief Acct, CFO, Auditor, CEO)',
      ],
      enhancements: [
        'Master Data Validation Service (duplicate prevention, parent validation)',
        'Import/Export utilities (CSV, JSON)',
        'Architecture Validator confirms zero hardcoded financial values',
      ],
      database: [
        '16 new finance tables (D1 schema)',
        '284 active master data records initialized',
      ],
      api: [
        'POST /api/finance/posting/events — Register accounting event',
        'POST /api/finance/posting/validate — Validate before posting',
        'POST /api/finance/posting/generate-journal — Generate journal draft',
        'GET /api/finance/posting/rules — Get posting rules',
        'POST /api/finance/posting/rules — Create posting rule',
        'PUT /api/finance/posting/rules/:id — Update posting rule',
        'GET /api/finance/posting/event-registry — Get event registry',
      ],
      knownIssues: [
        'Exchange rates not populated — treasury operations not yet started',
        'Fiscal years empty — period management is WP-03 scope',
        'Bank IBAN/account numbers blank — confidential data pending',
      ],
    },
  },
  {
    version: '1.0 Gold',
    date: '2026-06-29',
    sections: {
      newFeatures: [
        'Complete ERP implementation with 8 enterprise modules',
        'Enterprise Document Engine with PDF generation & digital signatures',
        'Financial Document Engine with ZATCA compliance',
        'Proforma Invoice system with 20-section template',
        'Real-time pipeline forecast dashboard',
        'Vendor performance scoring & comparison matrix',
        'ISO Certification Export Pack',
        'CEO Daily Digest & KPI Dashboard',
        'Board Reporting Pack',
        'Release Governance Dashboard',
      ],
      enhancements: [
        'Universal Authorization Middleware (UAM) at gateway level',
        'Role-based navigation visibility (13 role types)',
        'Currency-aware bank account validation',
        'Automated payment schedule generation',
        'Document traceability enforcement (40 module prefixes)',
      ],
      performance: [
        'Lazy-loaded routing for 90+ pages',
        'Configuration persistence with versioning',
        'Optimized database queries with D1',
      ],
      security: [
        '8-layer defense architecture',
        'Centralized permission engine (130+ route mappings)',
        'Digital signature verification',
        'Audit trail on all financial documents',
      ],
      database: [
        'D1 SQLite schema v1.0.0',
        'R2 object storage for document attachments',
        'Configuration versioning with rollback',
      ],
      api: [
        'RESTful API with Hono framework',
        'Universal Document Traceability API',
        'KPI Unification API (single source of truth)',
        'IAM & RBAC endpoints',
      ],
      knownIssues: [
        'DNS SPF/DKIM verification pending for email delivery',
        'MailChannels deprecation risk (migration path identified)',
      ],
    },
  },
];

// ── MODULE RELEASE STATUS ────────────────────────────────────────
export type ModuleStatus = 'Production' | 'In Development' | 'Planned' | 'Deprecated';
export type MigrationStatus = 'Complete' | 'In Progress' | 'Planned' | 'N/A';

export interface ModuleRelease {
  name: string;
  version: string;
  targetRelease: string;
  migrationStatus: MigrationStatus;
  productionStatus: ModuleStatus;
  documentEngineStatus: 'Active' | 'Pending' | 'N/A';
  lastUpdated: string;
  owner: string;
}

export const MODULE_REGISTER: ModuleRelease[] = [
  { name: 'Commercial', version: '1.0', targetRelease: '1.0', migrationStatus: 'Complete', productionStatus: 'Production', documentEngineStatus: 'Active', lastUpdated: '2026-06-29', owner: 'VP Commercial' },
  { name: 'Finance', version: '1.1.0', targetRelease: '1.1', migrationStatus: 'Complete', productionStatus: 'Production', documentEngineStatus: 'Active', lastUpdated: '2026-06-30', owner: 'CFO' },
  { name: 'Operations', version: '1.0', targetRelease: '1.2', migrationStatus: 'Complete', productionStatus: 'Production', documentEngineStatus: 'Active', lastUpdated: '2026-06-29', owner: 'COO' },
  { name: 'Procurement', version: '1.0', targetRelease: '1.0', migrationStatus: 'Complete', productionStatus: 'Production', documentEngineStatus: 'Active', lastUpdated: '2026-06-29', owner: 'Procurement Manager' },
  { name: 'QHSE & IMS', version: '1.0', targetRelease: '1.3', migrationStatus: 'Complete', productionStatus: 'Production', documentEngineStatus: 'Active', lastUpdated: '2026-06-29', owner: 'QHSE Manager' },
  { name: 'HR & People', version: '1.0', targetRelease: '1.4', migrationStatus: 'Complete', productionStatus: 'Production', documentEngineStatus: 'Pending', lastUpdated: '2026-06-29', owner: 'HR Manager' },
  { name: 'Governance', version: '1.0', targetRelease: '1.5', migrationStatus: 'Complete', productionStatus: 'Production', documentEngineStatus: 'Active', lastUpdated: '2026-06-29', owner: 'CEO' },
  { name: 'Multi-Company', version: '2.0', targetRelease: '2.0', migrationStatus: 'Planned', productionStatus: 'Planned', documentEngineStatus: 'N/A', lastUpdated: '2026-06-29', owner: 'CTO' },
  { name: 'API Marketplace', version: '2.1', targetRelease: '2.1', migrationStatus: 'Planned', productionStatus: 'Planned', documentEngineStatus: 'N/A', lastUpdated: '2026-06-29', owner: 'CTO' },
  { name: 'Mobile Applications', version: '2.2', targetRelease: '2.2', migrationStatus: 'Planned', productionStatus: 'Planned', documentEngineStatus: 'N/A', lastUpdated: '2026-06-29', owner: 'CTO' },
];

// ── PRODUCT ROADMAP ──────────────────────────────────────────────
export interface RoadmapItem {
  release: string;
  version: string;
  title: string;
  modules: string[];
  status: 'Completed' | 'In Progress' | 'Planned';
  targetDate?: string;
}

export const PRODUCT_ROADMAP: RoadmapItem[] = [
  { release: '1.0 Gold', version: '1.0', title: 'Commercial Module & Core Platform', modules: ['Commercial', 'Procurement', 'Operations', 'QHSE & IMS', 'HR', 'Finance', 'Executive', 'Document Control'], status: 'Completed', targetDate: '2026-06-29' },
  { release: '1.1.0', version: '1.1.0', title: 'Finance Foundation (WP-01) — FROZEN', modules: ['Finance'], status: 'Completed', targetDate: '2026-06-30' },
  { release: '1.1.1', version: '1.1.1', title: 'Accounting Posting Engine (WP-02)', modules: ['Finance'], status: 'Planned', targetDate: '2026-Q3' },
  { release: '1.2', version: '1.2', title: 'Operations Module Enhancement', modules: ['Operations'], status: 'Planned', targetDate: '2026-Q3' },
  { release: '1.3', version: '1.3', title: 'QHSE Module Enhancement', modules: ['QHSE & IMS'], status: 'Planned', targetDate: '2026-Q4' },
  { release: '1.4', version: '1.4', title: 'HR Module Enhancement', modules: ['HR & People'], status: 'Planned', targetDate: '2026-Q4' },
  { release: '2.0', version: '2.0', title: 'Enterprise Platform', modules: ['Multi-Company', 'Multi-Country', 'Advanced Workflow', 'Enterprise Analytics'], status: 'Planned', targetDate: '2027-Q1' },
  { release: '2.1', version: '2.1', title: 'API Marketplace', modules: ['API Marketplace'], status: 'Planned', targetDate: '2027-Q2' },
  { release: '2.2', version: '2.2', title: 'Mobile Applications', modules: ['Mobile Applications'], status: 'Planned', targetDate: '2027-Q3' },
];

// ── FEATURE FREEZE POLICY ────────────────────────────────────────
export const FEATURE_FREEZE = {
  version: '1.0 Gold',
  frozenDate: '2026-06-29',
  policy: [
    'Version 1.0 Gold is now frozen — no structural modifications permitted.',
    'All future changes must follow the formal Change Control process.',
    'No direct production modifications allowed.',
    'Every enhancement must belong to a planned release.',
  ],
  changeProcess: [
    'Feature Request',
    'Impact Assessment',
    'Approval',
    'Development Branch',
    'Testing',
    'Validation',
    'Release Candidate',
    'Production',
  ],
  releaseRequirements: [
    'Version Number',
    'Release Notes',
    'Validation Report',
    'Regression Test Report',
    'Deployment Approval',
    'Rollback Plan',
  ],
} as const;

// ── GO-LIVE CERTIFICATE ──────────────────────────────────────────
export const GO_LIVE_CERTIFICATE = {
  productName: PRODUCT.name,
  edition: PRODUCT.edition,
  version: PRODUCT.fullVersion,
  release: PRODUCT.build,
  certificationDate: '2026-06-29',
  validatedModules: [
    'Commercial Module',
    'Finance Module',
    'Operations Module',
    'Procurement Module',
    'QHSE & IMS Module',
    'HR & People Module',
    'Executive Dashboards',
    'Document Control Engine',
    'Governance Framework',
  ],
  productionStatus: 'Live',
  approvedBy: 'Managing Director — EnergiPro Solutions LLC',
  deploymentEnvironment: 'Cloudflare Workers (Edge)',
  infrastructure: {
    compute: 'Cloudflare Workers',
    database: 'Cloudflare D1 (SQLite)',
    storage: 'Cloudflare R2',
    cdn: 'Cloudflare CDN',
    auth: 'EdgeSpark Auth',
  },
} as const;
